import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { api, buildDropshipPriorityRequestBody, endpoints } from '../api/config';

const STATUS_CONFIG = [
  { key: 'active', label: 'Active', color: 'primary.main' },
  { key: 'unassigned', label: 'Unassigned', color: 'warning.main' },
  { key: 'unpicked', label: 'Unpicked', color: 'error.main' },
  { key: 'completed', label: 'Unavailable', color: 'success.main' },
  { key: 'pickSequence', label: 'Pick Sequence', color: 'info.main' }
];

const buildLtlPickSequenceColumns = () => ([
  { accessorKey: 'internal_id', header: 'Internal ID'},
  { accessorKey: 'ponumber', header: 'PO Number'},
  { accessorKey: 'picked_qty', header: 'Picked Qty', cell: ({ getValue }) => renderCountChip(getValue()) },
  { accessorKey: 'ready_for_pick', header: 'Ready Qty', cell: ({ getValue }) => renderCountChip(getValue()) },
  {
    accessorKey: 'total_qty',
    header: 'Total Qty',
    cell: ({ row, getValue }) => renderCountChip(getValue() ?? row?.original?.total_quantity ?? row?.original?.quantity ?? 0)
  },
  { accessorKey: 'priority', header: 'Priority'},
]);

const renderStatusChip = (value) => {
  if (!value) return '-';
  return (
    <Chip
      label={value}
      size="small"
      color="primary"
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
};

const renderCountChip = (value) => (
  <Chip
    label={value ?? 0}
    size="small"
    color="info"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

const renderPickedChip = (value) => {
  if (!value) return <Chip label="Pending" size="small" color="warning" variant="outlined" />;
  const val = String(value).toLowerCase();
  if (val === 'picked') return <Chip label="Picked" size="small" color="success" variant="outlined" />;
  return <Chip label={value} size="small" color="default" variant="outlined" />;
};

const renderInvChip = (value) => {
  const available = value === true || String(value).toLowerCase() === 'true';
  return (
    <Chip
      label={available ? 'Available' : 'Not Available'}
      size="small"
      color={available ? 'success' : 'error'}
      variant="outlined"
    />
  );
};

const buildColumns = (statusKey) => ([
  { accessorKey: 'ponumber', header: 'PO Number'},
  { accessorKey: 'sonumber', header: 'SO Number'},
  { accessorKey: 'internal_id', header: 'Internal ID'},
  {
    accessorKey: 'username',
    header: 'Picker',
    cell: ({ getValue }) => {
      const val = getValue();
      return statusKey === 'unassigned' ? '-' : (val || '-');
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => renderStatusChip(getValue())
  },
  {
    accessorKey: 'items_count',
    header: 'Items',
    cell: ({ getValue }) => renderCountChip(getValue())
  }
]);

const buildItemColumns = () => ([
  { accessorKey: 'item', header: 'Item'},
  { accessorKey: 'quantity', header: 'Qty'},
  { accessorKey: 'is_picked', header: 'Picked', cell: ({ getValue }) => renderPickedChip(getValue()) },
  { accessorKey: 'username', header: 'Picker'},
  { accessorKey: 'inv_available', header: 'Inventory', cell: ({ getValue }) => renderInvChip(getValue()) }
]);

const LTL_PICK_PROGRESS_LOCATION_STORAGE_KEY = 'ltlPickProgressSelectedLocation';
const LTL_GRID_PAGE_SIZE = 1000;

const sortPickSequenceByPriority = (items, getPriority) =>
  [...items].sort((a, b) => {
    const priorityDiff = getPriority(a) - getPriority(b);
    if (priorityDiff !== 0) return priorityDiff;
    return (a._pickSeqIndex ?? 0) - (b._pickSeqIndex ?? 0);
  });

const extractLocationNames = (locationsPayload) => {
  if (!locationsPayload) return [];
  const list = Array.isArray(locationsPayload)
    ? locationsPayload
    : Array.isArray(locationsPayload?.data)
      ? locationsPayload.data
      : locationsPayload?.data && typeof locationsPayload.data === 'object'
        ? Object.values(locationsPayload.data)
        : Array.isArray(locationsPayload?.results)
          ? locationsPayload.results
          : Array.isArray(locationsPayload?.records)
            ? locationsPayload.records
            : [];

  return list
    .map((loc) => {
      if (typeof loc === 'string') return loc;
      return (
        loc?.location ??
        loc?.name ??
        loc?.location_name ??
        loc?.locationName ??
        loc?.id ??
        loc?.location_id ??
        ''
      );
    })
    .filter(Boolean);
};

const PriorityEditorCell = React.memo(function PriorityEditorCell({ initialValue, onCommit, inputKey }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <TextField
        key={inputKey}
        size="small"
        type="number"
        defaultValue={String(initialValue ?? 0)}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => onCommit(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        inputProps={{ min: 0, step: 1 }}
        sx={{ width: 84, '& .MuiInputBase-input': { py: 0.6 } }}
      />
    </Box>
  );
});

const LtlPickProgress = () => {
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [location, setLocation] = useState(() => localStorage.getItem(LTL_PICK_PROGRESS_LOCATION_STORAGE_KEY) || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ active: [], unassigned: [], completed: [], unpicked: [], unavailable: [], total_records: 0 });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsItems, setDetailsItems] = useState([]);
  const [detailsTitle, setDetailsTitle] = useState('');
  
  // Search state for each table
  const [unpickedSearch, setUnpickedSearch] = useState('');
  const [unavailableSearch, setUnavailableSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [unassignedSearch, setUnassignedSearch] = useState('');
  const [detailsSearch, setDetailsSearch] = useState('');
  const [pickSeqSearch, setPickSeqSearch] = useState('');

  const [pickSeqData, setPickSeqData] = useState([]);
  const [pickSeqLoading, setPickSeqLoading] = useState(false);
  const [pickSeqError, setPickSeqError] = useState('');
  const [pickSeqSortVersion, setPickSeqSortVersion] = useState(0);
  const [saveLoading, setSaveLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Refs for scrolling to table sections
  const activeRef = useRef(null);
  const unassignedRef = useRef(null);
  const unpickedRef = useRef(null);
  const completedRef = useRef(null);
  const pickSequenceRef = useRef(null);
  const pendingPriorityByKeyRef = useRef(new Map());

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();
  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Fetch locations internally
  useEffect(() => {
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const resp = await api.get(endpoints.locations);
        const mapped = [...new Set(extractLocationNames(resp?.data ?? resp))].sort((a, b) =>
          a.localeCompare(b)
        );
        setLocations(mapped);
      } catch (e) {
        // surface but don't block the rest of the screen
        setError(e?.response?.data?.message || e?.message || 'Failed to load locations');
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locationsLoading) return;
    if (!locations.length) return;
    if (!location || !locations.includes(location)) {
      setLocation(locations[0]);
    }
  }, [locations, locationsLoading, location]);

  useEffect(() => {
    if (location) {
      localStorage.setItem(LTL_PICK_PROGRESS_LOCATION_STORAGE_KEY, location);
    }
  }, [location]);

  const fetchData = async (loc) => {
    if (!loc) return;
    setLoading(true);
    setError('');
    try {
      const resp = await api.get(`${endpoints.ltlPickProgress}?location=${encodeURIComponent(loc)}`);
      const payload = resp?.data || {};
      const normalizeList = (list) => Array.isArray(list) ? list.map(item => ({
        ...item,
        items_count: Array.isArray(item.data) ? item.data.length : 0
      })) : [];
      
      // Collect all items from all categories and filter for unpicked (is_picked === null) and unavailable (inv_available === false)
      const allItems = [];
      [payload.active, payload.unassigned, payload.completed].forEach(category => {
        if (Array.isArray(category)) {
          category.forEach(record => {
            if (Array.isArray(record.data)) {
              allItems.push(...record.data);
            }
          });
        }
      });
      
      const unpickedItems = allItems.filter(item => item?.is_picked === null || item?.is_picked === undefined);
      const unavailableItems = allItems.filter(item => {
        const invAvailable = item?.inv_available;
        return invAvailable === false || invAvailable === 'false' || String(invAvailable).toLowerCase() === 'false';
      });
      
      setData({
        total_records: payload.total_records || 0,
        active: normalizeList(payload.active),
        unassigned: normalizeList(payload.unassigned),
        completed: normalizeList(payload.completed),
        unpicked: unpickedItems,
        unavailable: unavailableItems,
      });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load pick progress');
    } finally {
      setLoading(false);
    }
  };

  const fetchPickSequence = async (loc) => {
    if (!loc) return;
    setPickSeqLoading(true);
    setPickSeqError('');
    try {
      const resp = await api.get(`${endpoints.ltlPickSequence}?location=${encodeURIComponent(loc)}`);
      const payload = resp?.data;
      const results = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      const withIndex = results.map((item, index) => ({ ...item, _pickSeqIndex: index }));
      pendingPriorityByKeyRef.current.clear();
      setPickSeqSortVersion(0);
      setPickSeqData(
        sortPickSequenceByPriority(withIndex, (item) => {
          const normalized = Number(item?.priority ?? 0);
          return Number.isFinite(normalized) ? normalized : 0;
        })
      );
    } catch (e) {
      setPickSeqError(e?.response?.data?.message || e?.message || 'Failed to load pick sequence');
    } finally {
      setPickSeqLoading(false);
    }
  };

  // Fetch when location changes
  useEffect(() => {
    if (location) {
      fetchData(location);
      fetchPickSequence(location);
    }
  }, [location]);

  useEffect(() => {
    if (sessionStorage.getItem('pickSequenceNeedsRefresh') === 'true' && location) {
      sessionStorage.removeItem('pickSequenceNeedsRefresh');
      fetchPickSequence(location);
    }
  }, [location]);

  const getPickSequenceRowKey = useCallback((row) => {
    if (!row) return '';
    if (row?._pickSeqIndex !== undefined) return `index:${row._pickSeqIndex}`;
    if (row?.unique_id) return `unique:${String(row.unique_id)}`;
    return `fallback:${String(row?.internal_id || '')}|${String(row?.ponumber || '')}`;
  }, []);

  const updatePickSequencePriority = useCallback(
    (row, nextValue) => {
      const normalized =
        nextValue === '' || nextValue === null || nextValue === undefined ? 0 : Number(nextValue);
      const safePriority = Number.isFinite(normalized) ? normalized : 0;
      const rowKey = getPickSequenceRowKey(row);
      if (!rowKey) return;
      pendingPriorityByKeyRef.current.set(rowKey, safePriority);
      setPickSeqSortVersion((version) => version + 1);
    },
    [getPickSequenceRowKey]
  );

  const sortedPickSeqData = useMemo(() => {
    const items = Array.isArray(pickSeqData) ? pickSeqData : [];
    return sortPickSequenceByPriority(items, (item) => {
      const pending = pendingPriorityByKeyRef.current.get(getPickSequenceRowKey(item));
      const normalized = Number(pending ?? item?.priority ?? 0);
      return Number.isFinite(normalized) ? normalized : 0;
    });
  }, [pickSeqData, pickSeqSortVersion, getPickSequenceRowKey]);

  const handleSavePickSeqOrder = async () => {
    const payload = (Array.isArray(pickSeqData) ? pickSeqData : [])
      .filter((item) => item.internal_id)
      .map((item) => {
        const pending = pendingPriorityByKeyRef.current.get(getPickSequenceRowKey(item));
        return {
          internal_id: item.internal_id,
          priority: pending ?? item.priority ?? 0,
        };
      });
    if (payload.length === 0) {
      setSnackbar({
        open: true,
        message: 'No items with internal_id found to update priorities.',
        severity: 'warning',
      });
      return;
    }
    setSaveLoading(true);
    try {
      await api.post(endpoints.dropshipUpdatePriority, buildDropshipPriorityRequestBody(payload));
      pendingPriorityByKeyRef.current.clear();
      await fetchPickSequence(location);
      setSnackbar({ open: true, message: 'Priorities updated successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || 'Failed to update priorities',
        severity: 'error',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const summary = useMemo(() => {
    const unpickedItems = data.unpicked || [];
    const unpickedCount = unpickedItems.length;
    const unpickedQty = unpickedItems.reduce((sum, item) => {
      const qty = parseInt(item?.quantity || 0, 10);
      return sum + (isNaN(qty) ? 0 : qty);
    }, 0);
    
    const unavailableItems = data.unavailable || [];
    const unavailableCount = unavailableItems.length;
    const unavailableQty = unavailableItems.reduce((sum, item) => {
      const qty = parseInt(item?.quantity || 0, 10);
      return sum + (isNaN(qty) ? 0 : qty);
    }, 0);
    
    const activeCount = data.active?.length || 0;
    const unassignedCount = data.unassigned?.length || 0;
    const completedCount = data.completed?.length || 0;
    const completedQty = data.completed?.reduce((sum, po) => {
      const poItems = po.data || [];
      return sum + poItems.reduce((itemSum, item) => {
        const qty = parseInt(item?.quantity || 0, 10);
        return itemSum + (isNaN(qty) ? 0 : qty);
      }, 0);
    }, 0);

    const pickSeqCount = pickSeqData?.length || 0;
    const pickSeqQty = pickSeqData?.reduce((sum, item) => {
      const total = parseInt(item?.total_qty ?? item?.total_quantity ?? item?.quantity, 10) || 0;
      const picked = parseInt(item?.picked_qty, 10) || 0;
      return sum + (total - picked);
    }, 0);

    const totalLines = activeCount + unassignedCount + unpickedCount + completedCount;

    return {
      total: totalLines,
      active: activeCount,
      unassigned: unassignedCount,
      unpicked: unpickedCount,
      unpickedQty: unpickedQty,
      completed: completedCount,
      completedQty: completedQty,
      unavailableQty: unavailableQty,
      pickSequence: pickSeqCount,
      pickSeqQty: pickSeqQty
    };
  }, [data, pickSeqData]);

  const openDetails = (title, items) => {
    // Open dialog immediately
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsSearch(''); // Reset search when opening details
    // Set data after a brief delay to show loading spinner
    setTimeout(() => {
      setDetailsTitle(title);
      setDetailsItems(Array.isArray(items) ? items : []);
      setDetailsLoading(false);
    }, 100);
  };

  // Helper function to filter items based on search term
  const filterItems = (items, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return items;
    const term = searchTerm.toLowerCase().trim();
    return items.filter(item => {
      // Search across all string/number fields
      return Object.values(item).some(value => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  };

  const itemGridColumns = useMemo(
    () =>
      buildItemColumns().map((col) => ({
        ...col,
        cell:
          col.cell ||
          ((info) => {
            const row = info.row.original;
            const key = col.accessorKey;
            const raw = row[key];
            return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
          }),
      })),
    []
  );

  const pickSeqGridColumns = useMemo(
    () =>
      buildLtlPickSequenceColumns().map((col) => {
        if (col.accessorKey === 'priority') {
          return {
            ...col,
            cell: (info) => {
              const rowKey = getPickSequenceRowKey(info.row.original);
              const currentValue =
                pendingPriorityByKeyRef.current.get(rowKey) ?? info.getValue() ?? 0;
              return (
                <PriorityEditorCell
                  inputKey={`${rowKey}-${currentValue}`}
                  initialValue={currentValue}
                  onCommit={(nextPriority) =>
                    updatePickSequencePriority(info.row.original, nextPriority)
                  }
                />
              );
            },
          };
        }
        return {
          ...col,
          cell:
            col.cell ||
            ((info) => {
              const raw = info.row.original[col.accessorKey];
              return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
            }),
        };
      }),
    [getPickSequenceRowKey, updatePickSequencePriority]
  );

  const poGridColumns = (statusKey) =>
    buildColumns(statusKey).map((col) => ({
      ...col,
      cell:
        col.cell ||
        ((info) => {
          const row = info.row.original;
          const key = col.accessorKey;
          const raw = row[key];
          return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
        }),
    }));

  const withStableRowIds = (rows, prefix) =>
    (Array.isArray(rows) ? rows : []).map((row, index) => {
      const baseId =
        row?.id ??
        row?._id ??
        row?.internal_id ??
        row?.transaction_id ??
        `${row?.item ?? row?.ponumber ?? 'row'}`;
      return {
        ...row,
        id: `${prefix}-${String(baseId)}-${index}`,
      };
    });
  const toDataGridColumns = (columns) =>
    (columns || []).map((col) => ({
      field: col.accessorKey,
      headerName: col.header,
      sortable: true,
      resizable: col.resizable !== false,
      flex: col.flex ?? (col.width ? undefined : 1),
      width: col.width,
      minWidth: col.minWidth ?? col.minSize ?? col.size ?? 80,
      renderCell: col.cell
        ? (params) =>
            col.cell({
              getValue: () => params.row?.[col.accessorKey],
              row: { original: params.row },
            })
        : (params) => {
            const raw = params.row?.[col.accessorKey];
            return raw === null || raw === undefined || raw === '' ? '-' : String(raw);
          },
    }));

  const locationSelectOptions = useMemo(() => {
    if (locationsLoading && location) return [location];
    if (locations.length) return locations;
    return location ? [location] : [];
  }, [locations, locationsLoading, location]);

  const locationSelectValue = locationSelectOptions.includes(location)
    ? location
    : (locationSelectOptions[0] ?? '');

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: statusFilter !== 'all' ? '100%' : 'auto', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        {statusFilter !== 'all' && (
          <IconButton
            onClick={() => setStatusFilter('all')}
            sx={{ mr: -1 }}
            aria-label="go back to cards view"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant="h5" sx={{ fontWeight: 600 }}>LTL Pick Progress</Typography>
        <FormControl size="small" sx={{ minWidth: 220, mt: 1 }}>
          <InputLabel id="pick-progress-location-label" shrink={!!locationSelectValue}>Location</InputLabel>
          <Select
            labelId="pick-progress-location-label"
            value={locationSelectValue}
            label="Location"
            onChange={(e) => setLocation(e.target.value)}
            disabled={locationsLoading || loading}
          >
            {locationSelectOptions.map((loc) => (
              <MenuItem key={loc} value={loc}>{loc}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180, mt: 1 }}>
          <InputLabel id="pick-progress-status-label" shrink={!!statusFilter}>Status</InputLabel>
          <Select
            labelId="pick-progress-status-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {STATUS_CONFIG.map(s => (
              <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading || pickSeqLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => {
            if (!location) return;
            fetchData(location);
            fetchPickSequence(location);
          }}
          disabled={!location || loading || pickSeqLoading}
          sx={{ height: '40px', mt: 1 }}
        >
          Refresh
        </Button>
        {(statusFilter === 'all' || statusFilter === 'pickSequence') && (
          <Button
            variant="contained"
            size="small"
            startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSavePickSeqOrder}
            disabled={saveLoading || pickSeqLoading || pickSeqData.length === 0}
            sx={{ height: '40px', mt: 1, flexShrink: 0 }}
          >
            {saveLoading ? 'Saving...' : 'Save order'}
          </Button>
        )}
        <GlobalDenseModeToolbar
          denseMode={denseMode}
          denseModeTogglePending={denseModeTogglePending}
          persistDenseMode={persistDenseMode}
          sx={{ mt: 1 }}
        />
      </Box>

      {statusFilter === 'all' && (
        <Box sx={{ mb: 3, flexShrink: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <Box sx={{ 
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(20% - 12.8px)' },
              minWidth: 0
            }}>
              <Card 
                elevation={2} 
                sx={{ 
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: 2,
                  '&:last-child': { pb: 2 }
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: 1.43
                    }}
                  >
                    lines
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '2.125rem',
                      lineHeight: 1.235
                    }}
                  >
                    {summary.total}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ 
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(20% - 12.8px)' },
              minWidth: 0
            }}>
              <Card 
                elevation={2} 
                onClick={() => {
                  setStatusFilter('active');
                  setTimeout(() => scrollToSection(activeRef), 100);
                }}
                sx={{ 
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: 2,
                  '&:last-child': { pb: 2 }
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: 1.43
                    }}
                  >
                    Live pickers
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'primary.main',
                      fontSize: '2.125rem',
                      lineHeight: 1.235
                    }}
                  >
                    {summary.active}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ 
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(20% - 12.8px)' },
              minWidth: 0
            }}>
              <Card 
                elevation={2} 
                onClick={() => {
                  setStatusFilter('unassigned');
                  setTimeout(() => scrollToSection(unassignedRef), 100);
                }}
                sx={{ 
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '4px solid',
                  borderColor: 'warning.main',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: 2,
                  '&:last-child': { pb: 2 }
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: 1.43
                    }}
                  >
                    orders
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'warning.main',
                      fontSize: '2.125rem',
                      lineHeight: 1.235
                    }}
                  >
                    {summary.unassigned}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ 
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(20% - 12.8px)' },
              minWidth: 0
            }}>
              <Card 
                elevation={2} 
                onClick={() => {
                  setStatusFilter('completed');
                  setTimeout(() => scrollToSection(completedRef), 100);
                }}
                sx={{ 
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '4px solid',
                  borderColor: 'success.main',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: 2,
                  '&:last-child': { pb: 2 }
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: 1.43
                    }}
                  >
                    Unavailable
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'success.main',
                      fontSize: '2.125rem',
                      lineHeight: 1.235
                    }}
                  >
                    {summary.completed}
                  </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}
                    >
                      Qty: {summary.unavailableQty}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ 
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(20% - 12.8px)' },
              minWidth: 0
            }}>
              <Card 
                elevation={2} 
                onClick={() => {
                  setStatusFilter('unpicked');
                  setTimeout(() => scrollToSection(unpickedRef), 100);
                }}
                sx={{ 
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '4px solid',
                  borderColor: 'error.main',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: 2,
                  '&:last-child': { pb: 2 }
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: 1.43
                    }}
                  >
                    unpicked lines
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'error.main',
                        fontSize: '2.125rem',
                        lineHeight: 1.235
                      }}
                    >
                      {summary.unpicked}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}
                    >
                      Qty: {summary.unpickedQty}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ 
              flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(20% - 12.8px)' },
              minWidth: 0
            }}>
              <Card 
                elevation={2} 
                onClick={() => {
                  setStatusFilter('pickSequence');
                  setTimeout(() => scrollToSection(pickSequenceRef), 100);
                }}
                sx={{ 
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '4px solid',
                  borderColor: 'info.main',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: 2,
                  '&:last-child': { pb: 2 }
                }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0.5, 
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: 1.43
                    }}
                  >
                    Pick Sequence
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'info.main',
                        fontSize: '2.125rem',
                        lineHeight: 1.235
                      }}
                    >
                      {summary.pickSequence}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }}
                    >
                      Qty: {summary.pickSeqQty}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2, color: 'error.main', flexShrink: 0 }}>{error}</Box>
      )}

      <Box sx={{ flex: statusFilter !== 'all' ? 1 : 'none', minHeight: statusFilter !== 'all' ? 0 : 'auto', display: 'flex', flexDirection: 'column', overflow: statusFilter !== 'all' ? 'hidden' : 'visible' }}>

      {(
        statusFilter === 'all'
          ? [
              ...STATUS_CONFIG.filter(s => s.key === 'pickSequence'),
              ...STATUS_CONFIG.filter(s => s.key !== 'pickSequence')
            ]
          : STATUS_CONFIG.filter(s => s.key === statusFilter)
      ).map(({ key, label, color }) => {
        // Unpicked is a flat list of items, not grouped by PO
        if (key === 'unpicked') {
          const unpickedItems = Array.isArray(data.unpicked) ? data.unpicked : [];
          const filteredUnpickedItems = filterItems(unpickedItems, unpickedSearch);
          return (
            <Box key={key} ref={unpickedRef} sx={{ 
              mb: 3, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 2, 
              p: 2, 
              backgroundColor: 'background.paper', 
              display: 'flex', 
              flexDirection: 'column', 
              flex: statusFilter !== 'all' ? 1 : 'none',
              minHeight: statusFilter !== 'all' ? 0 : 'auto'
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>({filteredUnpickedItems.length} lines, Qty: {summary.unpickedQty})</Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Search items..."
                  value={unpickedSearch}
                  onChange={(e) => setUnpickedSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 300 }}
                />
              </Stack>

              {filteredUnpickedItems.length === 0 ? (
                <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                  {unpickedItems.length === 0 ? 'No data available.' : 'No items match your search.'}
                </Box>
              ) : (
                <Box sx={{ flex: statusFilter !== 'all' ? 1 : 'none', minHeight: 0, width: '100%' }}>
                  <HoverExpandDataGridPro
                    disableVirtualization
                    rows={withStableRowIds(filteredUnpickedItems, 'unpicked')}
                    columns={toDataGridColumns(itemGridColumns)}
                    getRowId={(row) => String(row.id)}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                    initialState={{ pagination: { paginationModel: { pageSize: LTL_GRID_PAGE_SIZE, page: 0 } } }}
                    rowHeight={denseMode ? 36 : 52}
                    columnHeaderHeight={denseMode ? 40 : 56}
                    rowBufferPx={120}
                    columnBufferPx={120}
                    sx={{
                      height: statusFilter === 'all' ? 400 : '100%',
                      flex: statusFilter !== 'all' ? 1 : 'none',
                      minHeight: statusFilter !== 'all' ? 0 : 'auto',
                      maxHeight: statusFilter === 'all' ? '70vh' : 'none',
                    }}

                  />
                </Box>
              )}
            </Box>
          );
        }

        // Pick Sequence is a flat list of items
        if (key === 'pickSequence') {
          const filteredPickSeqItems = filterItems(sortedPickSeqData, pickSeqSearch);
          return (
            <Box key={key} ref={pickSequenceRef} sx={{
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              backgroundColor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              flex: statusFilter !== 'all' ? 1 : 'none',
              minHeight: statusFilter !== 'all' ? 0 : 'auto'
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2, flexShrink: 0, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>({filteredPickSeqItems.length} lines)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search pick sequence..."
                    value={pickSeqSearch}
                    onChange={(e) => setPickSeqSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 300 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    disabled={saveLoading || pickSeqLoading || pickSeqData.length === 0}
                    startIcon={saveLoading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    onClick={handleSavePickSeqOrder}
                    sx={{ flexShrink: 0 }}
                  >
                    {saveLoading ? 'Saving...' : 'Save order'}
                  </Button>
                </Box>
              </Stack>

              {pickSeqError ? (
                <Box sx={{ p: 2, color: 'error.main', textAlign: 'center' }}>{pickSeqError}</Box>
              ) : pickSeqLoading ? (
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
              ) : filteredPickSeqItems.length === 0 ? (
                <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                  {pickSeqData.length === 0 ? 'No data available.' : 'No items match your search.'}
                </Box>
              ) : (
                <Box sx={{ flex: statusFilter !== 'all' ? 1 : 'none', minHeight: 0, width: '100%' }}>
                  <HoverExpandDataGridPro
                    disableVirtualization
                    disableColumnSorting
                    rows={withStableRowIds(filteredPickSeqItems, 'pickseq')}
                    columns={toDataGridColumns(pickSeqGridColumns)}
                    loading={pickSeqLoading}
                    getRowId={(row) => String(row.id)}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                    initialState={{ pagination: { paginationModel: { pageSize: LTL_GRID_PAGE_SIZE, page: 0 } } }}
                    rowHeight={denseMode ? 36 : 52}
                    columnHeaderHeight={denseMode ? 40 : 56}
                    rowBufferPx={120}
                    columnBufferPx={120}
                    sx={{
                      height: statusFilter === 'all' ? 400 : '100%',
                      flex: statusFilter !== 'all' ? 1 : 'none',
                      minHeight: statusFilter !== 'all' ? 0 : 'auto',
                      maxHeight: statusFilter === 'all' ? '70vh' : 'none',
                    }}

                  />
                </Box>
              )}
            </Box>
          );
        }
        
        // Unavailable (completed) is a flat list of items, not grouped by PO
        if (key === 'completed') {
          const unavailableItems = Array.isArray(data.unavailable) ? data.unavailable : [];
          const filteredUnavailableItems = filterItems(unavailableItems, unavailableSearch);
          return (
            <Box key={key} ref={completedRef} sx={{ 
              mb: 3, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 2, 
              p: 2, 
              backgroundColor: 'background.paper', 
              display: 'flex', 
              flexDirection: 'column', 
              flex: statusFilter !== 'all' ? 1 : 'none',
              minHeight: statusFilter !== 'all' ? 0 : 'auto'
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>({filteredUnavailableItems.length} lines, Qty: {summary.unavailableQty})</Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Search items..."
                  value={unavailableSearch}
                  onChange={(e) => setUnavailableSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 300 }}
                />
              </Stack>

              {filteredUnavailableItems.length === 0 ? (
                <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                  {unavailableItems.length === 0 ? 'No data available.' : 'No items match your search.'}
                </Box>
              ) : (
                <Box sx={{ flex: statusFilter !== 'all' ? 1 : 'none', minHeight: 0, width: '100%' }}>
                  <HoverExpandDataGridPro
                    disableVirtualization
                    rows={withStableRowIds(filteredUnavailableItems, 'unavail')}
                    columns={toDataGridColumns(itemGridColumns)}
                    getRowId={(row) => String(row.id)}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                    initialState={{ pagination: { paginationModel: { pageSize: LTL_GRID_PAGE_SIZE, page: 0 } } }}
                    rowHeight={denseMode ? 36 : 52}
                    columnHeaderHeight={denseMode ? 40 : 56}
                    rowBufferPx={120}
                    columnBufferPx={120}
                    sx={{
                      height: statusFilter === 'all' ? 400 : '100%',
                      flex: statusFilter !== 'all' ? 1 : 'none',
                      minHeight: statusFilter !== 'all' ? 0 : 'auto',
                      maxHeight: statusFilter === 'all' ? '70vh' : 'none',
                    }}

                  />
                </Box>
              )}
            </Box>
          );
        }
        
        // Other categories are grouped by PO
        const rows = data[key] || [];
        const searchTerm = key === 'active' ? activeSearch : unassignedSearch;
        const filteredRows = filterItems(rows, searchTerm);
        const poCols = poGridColumns(key);
        const sectionRef = key === 'active' ? activeRef : key === 'unassigned' ? unassignedRef : null;
        return (
          <Box key={key} ref={sectionRef} sx={{ 
            mb: 3, 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 2, 
            p: 2, 
            backgroundColor: 'background.paper', 
            display: 'flex', 
            flexDirection: 'column', 
            flex: statusFilter !== 'all' ? 1 : 'none',
            minHeight: statusFilter !== 'all' ? 0 : 'auto'
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 2, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>({filteredRows.length})</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {rows.length > 0 ? 'Click a row to view its items.' : 'No data available.'}
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => {
                    if (key === 'active') setActiveSearch(e.target.value);
                    else setUnassignedSearch(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 300 }}
                />
              </Box>
            </Stack>

            {filteredRows.length === 0 ? (
              <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                {rows.length === 0 ? 'No data available.' : 'No orders match your search.'}
              </Box>
            ) : (
              <Box sx={{ flex: statusFilter !== 'all' ? 1 : 'none', minHeight: 0, width: '100%' }}>
                <HoverExpandDataGridPro
                  disableVirtualization
                  rows={withStableRowIds(filteredRows, key)}
                  columns={toDataGridColumns(poCols)}
                  getRowId={(row) => String(row.id)}
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                  pagination
                  paginationMode="client"
                  pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                  initialState={{ pagination: { paginationModel: { pageSize: LTL_GRID_PAGE_SIZE, page: 0 } } }}
                  rowHeight={denseMode ? 36 : 52}
                  columnHeaderHeight={denseMode ? 40 : 56}
                  rowBufferPx={120}
                  columnBufferPx={120}
                  onRowClick={(params) => {
                    const row = params.row;
                    const items = Array.isArray(row.data) ? row.data : [];
                    if (!items || items.length === 0) return;
                    openDetails(`${label} • PO ${row?.ponumber || 'N/A'}`, items);
                  }}
                  sx={{
                    height: statusFilter === 'all' ? 400 : '100%',
                    flex: statusFilter !== 'all' ? 1 : 'none',
                    minHeight: statusFilter !== 'all' ? 0 : 'auto',
                    maxHeight: statusFilter === 'all' ? '70vh' : 'none',
                  }}

                />
              </Box>
            )}
          </Box>
        );
      })}
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 5 }}>
            <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>{detailsTitle || 'Items'}</Typography>
            <TextField
              size="small"
              placeholder="Search items..."
              value={detailsSearch}
              onChange={(e) => setDetailsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 300 }}
            />
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setDetailsOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%', mt: 0.5 }}>
              <HoverExpandDataGridPro
                disableVirtualization
                rows={withStableRowIds(filterItems(detailsItems, detailsSearch), 'details')}
                columns={toDataGridColumns(itemGridColumns)}
                getRowId={(row) => String(row.id)}
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                pagination
                paginationMode="client"
                pageSizeOptions={[25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }]}
                initialState={{ pagination: { paginationModel: { pageSize: LTL_GRID_PAGE_SIZE, page: 0 } } }}
                rowHeight={denseMode ? 36 : 52}
                columnHeaderHeight={denseMode ? 40 : 56}
                rowBufferPx={120}
                columnBufferPx={120}
                sx={{ maxHeight: '60vh' }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default LtlPickProgress;

